/**
 * Pieces of the submissions pages: KPI tiles, a meter (a ratio against its
 * whole, the number always printed beside it), status chips (icon + label,
 * never colour alone).
 */

import React from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

export const FONT = "Be Vietnam Pro";
export const INK = { primary: "#16232e", secondary: "#4b5563", muted: "#6b7280" };
const BLUE = "#0172A6";
const TRACK = "#E5F1F6";

/** One headline number. */
export const Kpi = ({ label, value, hint }) => (
  <Box sx={{ flex: "1 1 120px", minWidth: 110, p: 1.5, border: "1px solid #e4eaf0", borderRadius: "10px", background: "#fff" }}>
    <Typography sx={{ fontSize: 12, color: INK.muted, fontFamily: FONT }}>{label}</Typography>
    <Typography sx={{ fontSize: 22, fontWeight: 600, color: INK.primary, fontFamily: FONT, lineHeight: 1.3 }}>{value}</Typography>
    {hint && <Typography sx={{ fontSize: 11, color: INK.muted }}>{hint}</Typography>}
  </Box>
);

export const KpiRow = ({ children }) => (
  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>{children}</Box>
);

/** A value out of 100, as a bar on a track of the same hue, with the number. */
export const Meter = ({ value, label, width = 72 }) => (
  <Tooltip title={label || (value === null || value === undefined ? "No data" : `${Math.round(value)}%`)}>
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ width, height: 6, borderRadius: 3, background: TRACK, overflow: "hidden" }}
           role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={value ?? undefined}>
        {value !== null && value !== undefined && (
          <Box sx={{ width: `${Math.max(0, Math.min(100, value))}%`, height: "100%", background: BLUE, borderRadius: 3 }} />
        )}
      </Box>
      <Typography component="span" sx={{ fontSize: 12, color: INK.secondary, minWidth: 32 }}>
        {value === null || value === undefined ? "—" : `${Math.round(value)}%`}
      </Typography>
    </Box>
  </Tooltip>
);

const STATUS = {
  not_started: { label: "Not started", icon: <RemoveCircleOutlineIcon />, color: "default" },
  not_submitted: { label: "Not submitted", icon: <RemoveCircleOutlineIcon />, color: "default" },
  in_progress: { label: "In progress", icon: <PlayCircleOutlineIcon />, color: "info" },
  completed: { label: "Completed", icon: <CheckCircleOutlineIcon />, color: "success" },
  submitted: { label: "To review", icon: <HourglassEmptyIcon />, color: "warning" },
  needs_review: { label: "To review", icon: <HourglassEmptyIcon />, color: "warning" },
  reviewed: { label: "Reviewed", icon: <CheckCircleOutlineIcon />, color: "success" },
  graded: { label: "Auto-graded", icon: <CheckCircleOutlineIcon />, color: "success" },
  needs_revision: { label: "Needs revision", icon: <AutorenewIcon />, color: "error" },
};

export const StatusChip = ({ status }) => {
  const s = STATUS[status] || { label: status, icon: null, color: "default" };
  return <Chip size="small" variant="outlined" color={s.color} icon={s.icon} label={s.label}
               sx={{ fontSize: 12, "& .MuiChip-icon": { fontSize: 16 } }} />;
};

/** "3 to review" badge for the overview. Nothing when there's nothing. */
export const PendingBadge = ({ count, what }) =>
  count > 0 ? (
    <Chip size="small" color="warning" variant="outlined" icon={<RateReviewOutlinedIcon />}
          label={`${count} ${what} to review`} sx={{ fontSize: 12, "& .MuiChip-icon": { fontSize: 16 } }} />
  ) : null;
